import { ActionIcon, Button, FileButton, Group, Loader, SegmentedControl, Slider, Stack, Text, Tooltip } from '@mantine/core'
import { useDebouncedValue, useElementSize } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconCamera, IconDeviceTablet, IconEraser, IconPhoto } from '@tabler/icons-react'
import { fileUrl } from '@tnn/core'
import type { KeptSignature, ScreenAsk } from '@tnn/domain'
import { ResponsiveDialog } from '@tnn/ui'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SignaturePadLib from 'signature_pad'
import { useMyScreen, useScreenGate } from '../screen/screen'
import { useHandoff } from '../tasks/handoff'
import { cleanSignature, inkBox, type Pixels } from './clean'
import classes from './signature.module.css'

/**
 * Signing on the screen: drawn with a finger or a pen, or a photo of one's signature on paper, cleaned (clean.ts). What
 * comes out is a transparent PNG cropped to the ink, for `POST /api/files`.
 */

/** A signature just made, not uploaded yet */
export interface NewSignature {
  blob: Blob
  method: KeptSignature['method']
}

/** Deep navy, like a ballpoint: dark enough to print and to read on screen */
const INK = '#1b2550'
/** A photo is cleaned at most this long on its long side: plenty for print, quick on a phone */
const PHOTO_SIDE = 1600

const toBlob = (canvas: HTMLCanvasElement) => new Promise<Blob | null>((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'))

function canvasOf(pixels: Pixels): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = pixels.width
  canvas.height = pixels.height
  canvas.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(pixels.data), pixels.width, pixels.height), 0, 0)
  return canvas
}

/** What is drawn on `canvas`, cropped to the ink with a little margin; null when nothing is */
async function trimmed(canvas: HTMLCanvasElement): Promise<Blob | null> {
  const context = canvas.getContext('2d')
  if (!context) return null
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height)
  const box = inkBox(pixels, Math.round(canvas.width * 0.02))
  if (!box) return null
  const out = document.createElement('canvas')
  out.width = box.width
  out.height = box.height
  out.getContext('2d')?.putImageData(pixels, -box.x, -box.y)
  return toBlob(out)
}

/** A photo's pixels, turned the way it was taken and no longer than `PHOTO_SIDE` */
async function photoPixels(file: File): Promise<Pixels> {
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    const scale = Math.min(1, PHOTO_SIDE / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(image.naturalWidth * scale)
    canvas.height = Math.round(image.naturalHeight * scale)
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) throw new Error('no 2d context')
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return context.getImageData(0, 0, canvas.width, canvas.height)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** An object URL for `blob` while it is shown */
export function useBlobUrl(blob: Blob | null) {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    if (!blob) return setUrl(undefined)
    const made = URL.createObjectURL(blob)
    setUrl(made)
    return () => URL.revokeObjectURL(made)
  }, [blob])
  return url
}

/**
 * A signature drawn above a dotted line; `onChange` after every stroke with what is drawn (null once cleared). With
 * `screen` (someone other than the staff member signs), it is drawn on the staff member's portable screen when they
 * have one, and they can take it back to this pad; when they have none, a button pairs one
 */
export function SignaturePad({
  onChange,
  screen,
  large,
}: {
  onChange: (blob: Blob | null) => void
  /** What the portable screen shows above its pad */
  screen?: ScreenAsk
  /** Taller, for the portable screen's own pad */
  large?: boolean
}) {
  const { t } = useTranslation()
  const gate = useScreenGate(!!screen)
  const [here, setHere] = useState(false)
  if (screen && gate.screen && !here) {
    return (
      <ScreenSigning
        ask={screen}
        onChange={onChange}
        onHere={() => {
          onChange(null)
          setHere(true)
        }}
      />
    )
  }
  return (
    <Stack gap={6}>
      <DrawnPad onChange={onChange} large={large} />
      {screen && (
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconDeviceTablet size={16} />}
            onClick={() => gate.run(() => setHere(false))}
          >
            {t(gate.screen ? 'screen.signThere' : 'screen.pair')}
          </Button>
        </Group>
      )}
      {gate.dialog}
    </Stack>
  )
}

/**
 * Waiting for the portable screen: the ask goes there when this shows and is taken back when it goes (`useHandoff`);
 * the signature, once confirmed there, shows here and is the pad's. It may be asked again, or taken back to this pad
 */
function ScreenSigning({ ask, onChange, onHere }: { ask: ScreenAsk; onChange: (blob: Blob | null) => void; onHere: () => void }) {
  const { t } = useTranslation()
  const screen = useMyScreen().data
  const [blob, setBlob] = useState<Blob | null>(null)
  const [round, setRound] = useState(0)
  const url = useBlobUrl(blob)
  const changed = useRef(onChange)
  changed.current = onChange
  const asked = useRef(ask)
  asked.current = ask
  const handoff = useHandoff({
    kind: 'signature',
    label: ask.title,
    onDone: async (task) => {
      const fileId = task.fileIds[0]
      if (!fileId) return
      // The screen uploaded it as this person's: fetched back, it goes on as if drawn on this pad
      // Same-origin cookies only: the file comes from a link to its bucket, which takes none
      const drawn = await fetch(fileUrl(fileId)).then((r) => r.blob())
      setBlob(drawn)
      changed.current(drawn)
    },
  })
  const askScreen = handoff.ask
  const myId = handoff.myId

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new round asks again
  useEffect(() => {
    if (!myId) return
    askScreen(myId, undefined, asked.current).catch(() => notifications.show({ color: 'red', message: t('task.failed') }))
  }, [round, myId])

  const again = () => {
    setBlob(null)
    changed.current(null)
    setRound((n) => n + 1)
  }

  return (
    <Stack gap={6}>
      <div className={`${classes.paper} ${classes.shown} ${classes.preview}`}>
        {url ? (
          <img src={url} alt={t('signature.alt')} />
        ) : (
          <Stack align="center" gap={6}>
            <Loader size="sm" color="gray" />
            <Text size="sm" fw={500}>
              {t('screen.waitingSign')}
            </Text>
            {screen && (
              <Text size="xs" c={screen.online ? undefined : 'var(--tnn-tone-warning-fg)'}>
                {t(screen.online ? 'screen.waitingOn' : 'screen.closed', { name: screen.name })}
              </Text>
            )}
          </Stack>
        )}
      </div>
      <Group justify="flex-end" gap="xs">
        {url && (
          <Button variant="subtle" size="compact-sm" onClick={again}>
            {t('screen.again')}
          </Button>
        )}
        <Button variant="subtle" size="compact-sm" onClick={onHere}>
          {t('screen.signHere')}
        </Button>
      </Group>
    </Stack>
  )
}

/** Drawing on this window's screen */
function DrawnPad({ onChange, large }: { onChange: (blob: Blob | null) => void; large?: boolean }) {
  const { t } = useTranslation()
  const { ref: box, width } = useElementSize()
  const canvas = useRef<HTMLCanvasElement>(null)
  const pad = useRef<SignaturePadLib | null>(null)
  const changed = useRef(onChange)
  changed.current = onChange
  const [drawn, setDrawn] = useState(false)

  useEffect(() => {
    if (!canvas.current) return
    const made = new SignaturePadLib(canvas.current, { penColor: INK, minWidth: 0.9, maxWidth: 2.8, velocityFilterWeight: 0.7 })
    const ended = () => {
      setDrawn(!made.isEmpty())
      if (canvas.current) void trimmed(canvas.current).then((blob) => changed.current(blob))
    }
    made.addEventListener('endStroke', ended)
    pad.current = made
    return () => {
      made.removeEventListener('endStroke', ended)
      made.off()
    }
  }, [])

  // The canvas at the screen's density, as wide as its box; what is drawn stays when it changes
  useEffect(() => {
    const element = canvas.current
    const drawing = pad.current
    if (!element || !drawing || !width) return
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const strokes = drawing.toData()
    element.width = Math.round(width * ratio)
    element.height = Math.round(element.offsetHeight * ratio)
    element.getContext('2d')?.scale(ratio, ratio)
    drawing.clear()
    drawing.fromData(strokes)
  }, [width])

  const clear = () => {
    pad.current?.clear()
    setDrawn(false)
    onChange(null)
  }

  return (
    <div ref={box} className={`${classes.paper} ${classes.pad}`} data-drawn={drawn || undefined} data-large={large || undefined}>
      <div className={classes.hint}>{t('signature.drawHint')}</div>
      <div className={classes.baseline}>{t('signature.signHere')}</div>
      <canvas ref={canvas} aria-label={t('signature.draw')} />
      {drawn && (
        <Tooltip label={t('signature.clear')}>
          <ActionIcon className={classes.corner} variant="subtle" color="gray" onClick={clear} aria-label={t('signature.clear')}>
            <IconEraser size={18} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  )
}

/** A photo of one's signature on paper: chosen or taken, cleaned, its strength adjustable */
export function SignaturePhoto({ onChange }: { onChange: (blob: Blob | null) => void }) {
  const { t } = useTranslation()
  const [source, setSource] = useState<Pixels | null>(null)
  const [strength, setStrength] = useState(50)
  const [settled] = useDebouncedValue(strength, 150)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Blob | null | 'none'>(null)
  const changed = useRef(onChange)
  changed.current = onChange
  const url = useBlobUrl(result instanceof Blob ? result : null)

  const choose = async (file: File | null) => {
    if (!file) return
    setBusy(true)
    try {
      setSource(await photoPixels(file))
    } catch {
      setResult('none')
      setBusy(false)
    }
  }

  // Cleaned again whenever the photo or the strength changes, after the loader has shown
  useEffect(() => {
    if (!source) return
    setBusy(true)
    const timer = window.setTimeout(async () => {
      const cleaned = cleanSignature(source, { sensitivity: settled / 100 })
      const blob = cleaned ? await toBlob(canvasOf(cleaned)) : null
      setResult(blob ?? 'none')
      changed.current(blob)
      setBusy(false)
    }, 30)
    return () => window.clearTimeout(timer)
  }, [source, settled])

  const picker = (children: (open: () => void) => ReactNode) => (
    <FileButton onChange={choose} accept="image/*">
      {(props) => children(props.onClick)}
    </FileButton>
  )

  if (!source && !busy)
    return picker((open) => (
      <button type="button" className={classes.pick} onClick={open}>
        <span className={classes.pickIcon}>
          <IconCamera size={24} />
        </span>
        <span className={classes.pickTitle}>{t('signature.choosePhoto')}</span>
        <span className={classes.pickTips}>{result === 'none' ? t('signature.notFound') : t('signature.photoTips')}</span>
      </button>
    ))

  return (
    <Stack gap="sm">
      <div className={`${classes.paper} ${classes.shown} ${classes.preview}`}>
        {busy ? (
          <Stack align="center" gap="xs">
            <Loader type="dots" />
            <Text size="sm" c="rgb(22 29 28 / 70%)">
              {t('signature.cleaning')}
            </Text>
          </Stack>
        ) : result === 'none' ? (
          <Text size="sm" ta="center" c="rgb(22 29 28 / 70%)" maw="34ch">
            {t('signature.notFound')}
          </Text>
        ) : (
          url && <img src={url} alt={t('signature.alt')} />
        )}
      </div>
      <Group gap="md" wrap="nowrap" align="center">
        <Stack gap={2} style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">
            {t('signature.strength')}
          </Text>
          <Slider value={strength} onChange={setStrength} label={null} size="sm" disabled={!source} aria-label={t('signature.strength')} />
        </Stack>
        {picker((open) => (
          <Button variant="subtle" leftSection={<IconPhoto size={18} />} onClick={open}>
            {t('signature.otherPhoto')}
          </Button>
        ))}
      </Group>
    </Stack>
  )
}

/** Drawing it or a photo of it, as the person prefers; `onChange` with the signature as it stands */
export function SignatureCapture({
  onChange,
  methods = ['drawn', 'photo'],
  screen,
}: {
  onChange: (signature: NewSignature | null) => void
  /** The ways it may be made (a job application: a photo of it on paper only), the first chosen first */
  methods?: readonly NewSignature['method'][]
  /** Someone else signs: drawn on the staff member's portable screen (`SignaturePad`) */
  screen?: ScreenAsk
}) {
  const { t } = useTranslation()
  const [method, setMethod] = useState<NewSignature['method']>(methods[0] ?? 'drawn')
  const take = (blob: Blob | null) => onChange(blob && { blob, method })
  return (
    <Stack gap="sm">
      {methods.length > 1 && (
        <SegmentedControl
          fullWidth
          value={method}
          onChange={(value) => {
            setMethod(value as NewSignature['method'])
            onChange(null)
          }}
          data={methods.map((value) => ({ value, label: t(value === 'drawn' ? 'signature.draw' : 'signature.photo') }))}
        />
      )}
      {method === 'drawn' ? <SignaturePad onChange={take} screen={screen} /> : <SignaturePhoto onChange={take} />}
    </Stack>
  )
}

/**
 * Signing in a dialog (a sheet from the bottom on a phone): `intro` says what is being signed; `onConfirm` takes the
 * signature (upload it, keep it) and the dialog closes once it resolves
 */
export function SignatureDialog({
  opened,
  onClose,
  title,
  intro,
  confirmLabel,
  onConfirm,
  methods,
  screen,
}: {
  opened: boolean
  onClose: () => void
  title: ReactNode
  intro?: ReactNode
  confirmLabel?: string
  onConfirm: (signature: NewSignature) => Promise<unknown> | unknown
  /** The ways it may be made (`SignatureCapture`) */
  methods?: readonly NewSignature['method'][]
  /** Someone else signs: on the staff member's portable screen (`SignaturePad`) */
  screen?: ScreenAsk
}) {
  const { t } = useTranslation()
  const [signature, setSignature] = useState<NewSignature | null>(null)
  const [saving, setSaving] = useState(false)
  // Each opening starts blank
  useEffect(() => {
    if (opened) setSignature(null)
  }, [opened])
  const confirm = async () => {
    if (!signature) return
    setSaving(true)
    try {
      await onConfirm(signature)
      onClose()
    } finally {
      setSaving(false)
    }
  }
  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!signature} loading={saving} onClick={confirm}>
            {confirmLabel ?? t('signature.use')}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        {intro}
        {opened && <SignatureCapture onChange={setSignature} methods={methods} screen={screen} />}
      </Stack>
    </ResponsiveDialog>
  )
}

/** A signature on its paper, 96px tall (an uploaded one by id, or one just made by `src`) */
export function SignatureImage({ fileId, src }: { fileId?: string; src?: string }) {
  const { t } = useTranslation()
  const url = src ?? (fileId ? fileUrl(fileId) : undefined)
  return <div className={`${classes.paper} ${classes.shown} ${classes.chosen}`}>{url && <img src={url} alt={t('signature.alt')} />}</div>
}
